import React, { useMemo } from "react";
import { useAHR999Chart } from "@/hooks/coinglass/useAHR999Chart";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import type { EChartsOption } from "echarts";

interface AHR999ChartProps {
  startDate?: string;
  endDate?: string;
  limit?: number;
  height?: string | number;
}

export const AHR999Chart: React.FC<AHR999ChartProps> = ({
  startDate,
  endDate,
  limit,
  height = 500,
}) => {
  const { data, isLoading, isError, error } = useAHR999Chart({
    start_date: startDate,
    end_date: endDate,
    limit: limit,
  });

  const option = useMemo<EChartsOption | null>(() => {
    if (!data) return null;

    const { xAxis, series } = data.data;
    
    return {
      // Title removed as requested
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderColor: '#333',
        textStyle: {
          color: '#fff'
        },
        formatter: (params: echarts.TooltipComponentFormatterCallbackParams) => {
          if (!Array.isArray(params) || params.length === 0) return "";
          
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const p = params as any[];
          const date = p[0]?.axisValue;
          const ahr999 = p[0]?.data as number;
          const price = p[1]?.data as number | undefined;

          if (ahr999 === undefined) return "";

          let zone = 'Normal Zone';
          if (ahr999 < 0.45) zone = '🟢 Strong Buy Zone';
          else if (ahr999 < 1.2) zone = '🟡 Reasonable Buy Zone';
          else zone = '🔴 High Valuation Zone';

          return `
            <div style="padding: 8px;">
              <div style="font-weight: bold; margin-bottom: 8px;">${date}</div>
              <div>AHR999 Index: ${ahr999.toFixed(4)}</div>
              ${price !== undefined ? `<div>BTC Price: $${price.toLocaleString()}</div>` : ''}
              <div style="margin-top: 8px; color: #fbbf24;">${zone}</div>
            </div>
          `;
        }
      },
      legend: {
        data: ['AHR999 Index', 'BTC Price'],
        textStyle: {
          color: '#ffffff'
        },
        top: 0, 
        left: "center"
      },
      grid: {
        left: '3%',
        right: '3%',
        bottom: '10%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xAxis,
        axisLabel: {
          color: '#9ca3af',
          hideOverlap: true,
          interval: 'auto', 
          formatter: (value: string) => {
            const date = new Date(value);
            return date.toLocaleDateString("en-US", {
              month: "short",
              year: "numeric",
            });
          },
        },
        axisLine: {
          lineStyle: {
            color: '#374151'
          }
        }
      },
      yAxis: [
        {
          type: 'value',
          name: 'AHR999 Index',
          position: 'left',
          nameTextStyle: { color: '#9ca3af' }, 
          axisLabel: {
            color: '#9ca3af',
            formatter: '{value}'
          },
          axisLine: {
            lineStyle: {
              color: '#374151' 
            }
          },
          splitLine: {
            lineStyle: {
              color: '#1f2937'
            }
          }
        },
        {
          type: 'value',
          name: 'BTC Price (USD)',
          position: 'right',
          nameTextStyle: { color: '#9ca3af' }, 
          axisLabel: {
            color: '#9ca3af',
            formatter: (value: number) => {
              if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
              return `$${value}`;
            }
          },
          axisLine: {
            lineStyle: {
              color: '#374151' 
            }
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: series.map((s, index) => ({
        ...s,
        type: 'line',
        itemStyle: {
          color: index === 0 ? '#10b981' : '#fbbf24'
        },
        symbol: 'none',
        markLine: index === 0 ? {
          silent: true,
          symbol: "none",
          z: 1000, // High z-index to ensure visibility
          data: [
            {
              yAxis: 1.2,
              label: { 
                formatter: "High (1.2)",
                position: "end", // Reverted to end
                color: "#ef4444"
              },
              lineStyle: { color: "rgba(239, 68, 68, 0.6)", type: "dashed" }
            },
            {
              yAxis: 0.45,
              label: { 
                formatter: "Buy (0.45)",
                position: "end", // Reverted to end
                color: "#10b981"
              },
              lineStyle: { color: "rgba(16, 185, 129, 0.6)", type: "dashed" }
            }
          ]
        } : undefined
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      })) as any,
      backgroundColor: 'transparent',
      dataZoom: [
        {
          type: 'inside',
          start: 80,  
          end: 100
        },
        {
          type: 'slider',
          start: 80,
          end: 100,
          textStyle: {
            color: '#888'
          },
          handleStyle: {
            color: '#555'
          },
          backgroundColor: '#252525',
          fillerColor: 'rgba(16, 185, 129, 0.2)',
          borderColor: '#333'
        }
      ]
    };
  }, [data]);

  return (
    <EChartsRenderer
      chartOption={option || {}}
      height={height}
      isLoading={isLoading}
      error={isError ? (error as Error).message : null}
      hasData={!!data}
      containerClassName="w-full h-full"
    />
  );
};
